import styled from "styled-components";

import { Button, Size, Variant } from "../../../components/Buttons";

export const DashboardView = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  padding: ${({ theme }) => theme.spacing.x_medium }px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: ${({ theme }) => theme.colors.secondary.hover } transparent;
  scrollbar-width: thin;
  color: ${({ theme }) => theme.colors.text.default };

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.secondary.hover };
    border-radius: 8px;
  }

  & > * {
    flex-shrink: 0;
  }

  @media ( max-width: 768px ) {
    padding: ${({ theme }) => theme.spacing.xx_small }px;
  }
`;

export const DashboardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.x_medium }px;
  min-height: 52px;
  padding-bottom: ${({ theme }) => theme.spacing.xxx_small }px;
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };

  & .dashboard-header-subtitle {
    display: block;
    margin-top: ${({ theme }) => theme.spacing.xx_small }px;
    color: ${({ theme }) => theme.colors.text.default }B3;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & .dashboard-header-title {
    margin: 0;
    min-height: ${({ theme }) => theme.font_size.x_large }px;
    font-size: ${({ theme }) => theme.font_size.x_large }px;
    line-height: ${({ theme }) => theme.font_size.x_large }px;
  }

  @media ( max-width: 768px ) {
    align-items: stretch;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xxx_small }px;

    & > div,
    & > form {
      width: 100%;
    }
  }
`;

export const FilterBar = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.xx_small }px;

  & .filter-field {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    color: ${({ theme }) => theme.colors.text.default }B3;
  }

  & .filter-input {
    height: ${({ theme }) => theme.spacing.xxx_large }px;
    padding: 0 ${({ theme }) => theme.spacing.xx_small }px;
    border-radius: ${({ theme }) => theme.border_radius.small }px;
    border: 1px solid ${({ theme }) => theme.glass.border };
    background-color: ${({ theme }) => theme.glass.elevated };
    color: ${({ theme }) => theme.colors.text.default };
    outline: none;

    &:focus-visible {
      border-color: ${({ theme }) => theme.colors.info.default };
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.info.default }33;
    }
  }

  @media ( max-width: 768px ) {
    display: grid;
    grid-template-columns: repeat( 2, minmax( 0, 1fr )) auto;

    & .filter-input {
      width: 100%;
    }
  }

  @media ( max-width: 480px ) {
    grid-template-columns: 1fr 1fr;

    & > button {
      grid-column: 1 / -1;
      width: 100%;
    }
  }
`;

export const DashboardTabs = styled.div`
  display: flex;
  width: fit-content;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  padding-bottom: ${({ theme }) => theme.spacing.x_small }px;
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };

  @media ( max-width: 640px ) {
    width: 100%;

    & .dashboard-tab-button {
      flex: 1;
      min-width: 0;
    }
  }
`;

export const DashboardTabButton = styled(Button).attrs({
  $color: "info",
  $size: Size.SMALL,
  $variant: Variant.TEXT,
})<{
  $active?: boolean;
}>`
  min-width: 112px;
  border: 0;
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.colors.info.default : "transparent" };
  border-radius: ${({ theme }) => theme.border_radius.small }px ${({ theme }) => theme.border_radius.small }px 0 0;
  background-color: ${({ theme, $active }) => $active ? theme.colors.info.default + "18" : "transparent" };
  color: ${({ theme, $active }) => $active ? theme.colors.info.default : theme.colors.text.default + "B3" };

  &:hover {
    border: 0;
    border-bottom: 2px solid ${({ theme }) => theme.colors.info.default };
    background-color: ${({ theme }) => theme.colors.info.default }18;
    color: ${({ theme }) => theme.colors.info.default };
  }
`;

export const NowControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.x_small }px;

  @media ( max-width: 768px ) {
    justify-content: flex-end;
  }
`;

export const NowTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  overflow: hidden;
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };

  & .now-tab-button {
    min-width: 220px;
  }

  @media ( max-width: 760px ) {
    & .now-tab-button {
      flex: 1 1 100%;
      min-width: 0;
    }
  }
`;

export const NowTabButton = styled(Button).attrs({
  $color: "info",
  $size: Size.SMALL,
  $variant: Variant.TEXT,
})<{
  $active?: boolean;
}>`
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing.xxx_small }px ${({ theme }) => theme.spacing.x_medium }px;
  border: 0;
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.colors.info.default : "transparent" };
  border-radius: 0;
  background: transparent;
  color: ${({ theme, $active }) => $active ? theme.colors.info.default : theme.colors.text.default };

  &:hover {
    border: 0;
    border-bottom: 2px solid ${({ theme }) => theme.colors.info.default };
    background-color: ${({ theme }) => theme.colors.text.default }08;
    color: ${({ theme }) => theme.colors.info.default };
  }
`;

export const NowStatus = styled.span`
  color: ${({ theme }) => theme.colors.text.default }99;
  font-size: ${({ theme }) => theme.font_size.xx_small }px;
`;

export const NowSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat( auto-fit, minmax( 170px, 1fr ));
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
`;

export const NowCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat( 3, minmax( 0, 1fr ));
  gap: ${({ theme }) => theme.spacing.xxx_small }px;

  @media ( max-width: 1180px ) {
    grid-template-columns: repeat( 2, minmax( 0, 1fr ));
  }

  @media ( max-width: 760px ) {
    grid-template-columns: 1fr;
  }
`;

export const NowGroupCard = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 320px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme }) => theme.glass.background };
  border: 1px solid ${({ theme }) => theme.glass.border };
  box-shadow: ${({ theme }) => theme.shadow.card };
`;

export const NowCardHeader = styled.div<{
  $status?: "online" | "offline" | "neutral";
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  min-height: 56px;
  padding: ${({ theme }) => theme.spacing.xxx_small }px ${({ theme }) => theme.spacing.x_medium }px;
  background-color: ${({ theme }) => theme.colors.text.default }05;
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };

  & .now-card-icon {
    flex-shrink: 0;
    color: ${({ theme, $status }) => $status === "online" ? theme.colors.success.default : $status === "offline" ? theme.colors.error.default : theme.colors.text.default };
  }

  & .now-card-title {
    min-width: 0;
  }

  & .now-card-title strong,
  & .now-card-title span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .now-card-title strong {
    font-size: ${({ theme }) => theme.font_size.x_small }px;
  }

  & .now-card-title span {
    margin-top: ${({ theme }) => theme.spacing.x_small }px;
    color: ${({ theme, $status }) => $status === "online" ? theme.colors.success.default : $status === "offline" ? theme.colors.error.default : theme.colors.text.default + "99" };
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
  }
`;

export const NowCardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 280px;
  overflow-y: auto;
  scrollbar-color: ${({ theme }) => theme.colors.secondary.hover } transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.secondary.hover };
    border-radius: 8px;
  }
`;

export const NowServiceRow = styled.div`
  display: grid;
  grid-template-columns: 42px minmax( 0, 1fr ) auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  padding: ${({ theme }) => theme.spacing.xx_small }px ${({ theme }) => theme.spacing.xxx_small }px;
  border-bottom: 1px solid ${({ theme }) => theme.glass.border };

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.text.default }08;
  }

  & .service-main {
    min-width: 0;
  }

  & .service-title {
    display: grid;
    grid-template-columns: minmax( 0, 1fr ) auto;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    align-items: center;
  }

  & .service-contact-name,
  & .service-preview,
  & .service-organization {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .service-contact-name {
    display: block;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
  }

  & .service-date,
  & .service-organization {
    color: ${({ theme }) => theme.colors.text.default }99;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & .service-preview {
    margin: ${({ theme }) => theme.spacing.x_small }px 0 0;
    color: ${({ theme }) => theme.colors.text.default }D9;
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
  }

  & .service-unread-badge {
    justify-self: end;
  }
`;

export const NowCardFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.xxx_small }px ${({ theme }) => theme.spacing.x_medium }px;
  border-top: 1px solid ${({ theme }) => theme.glass.border };
  background-color: ${({ theme }) => theme.colors.text.default }08;
  font-size: ${({ theme }) => theme.font_size.x_small }px;
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat( auto-fit, minmax( 180px, 1fr ));
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
`;

export const DashboardHalfSections = styled.div`
  display: grid;
  grid-template-columns: repeat( 2, minmax( 0, 1fr ));
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  min-height: 0;

  @media ( max-width: 980px ) {
    grid-template-columns: 1fr;
  }
`;

export const MetricCard = styled.div`
  padding: ${({ theme }) => theme.spacing.xxx_small }px ${({ theme }) => theme.spacing.x_medium }px;
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme }) => theme.glass.background };
  border: 1px solid ${({ theme }) => theme.glass.border };
  border-left: 2px solid ${({ theme }) => theme.colors.info.default };
  box-shadow: ${({ theme }) => theme.shadow.card };
  backdrop-filter: ${({ theme }) => theme.glass.blur };

  & span {
    display: block;
    color: ${({ theme }) => theme.colors.text.default }B3;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  & strong {
    display: block;
    margin-top: ${({ theme }) => theme.spacing.x_small }px;
    font-size: ${({ theme }) => theme.font_size.x_large }px;
    line-height: ${({ theme }) => theme.font_size.x_large }px;
  }
`;

export const DashboardSections = styled.div`
  display: grid;
  grid-template-columns: minmax( 0, 1.15fr ) minmax( 320px, .85fr );
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  min-height: 0;

  @media ( max-width: 980px ) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxx_small }px;
  padding: ${({ theme }) => theme.spacing.x_medium }px;
  border-radius: ${({ theme }) => theme.border_radius.medium }px;
  background-color: ${({ theme }) => theme.glass.background };
  border: 1px solid ${({ theme }) => theme.glass.border };
  box-shadow: ${({ theme }) => theme.shadow.card };
  backdrop-filter: ${({ theme }) => theme.glass.blur };
  min-width: 0;

  & h2 {
    margin: 0;
    font-size: ${({ theme }) => theme.font_size.x_medium }px;
    line-height: ${({ theme }) => theme.font_size.x_medium }px;
    padding-bottom: ${({ theme }) => theme.spacing.xx_small }px;
    border-bottom: 1px solid ${({ theme }) => theme.glass.border };
  }

  & ${ MetricsGrid } ${ MetricCard } {
    border-top: 0;
    border-right: 0;
    border-bottom: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
  }
`;

export const ChartFrame = styled.div<{
  $height: number;
}>`
  width: 100%;
  height: ${({ $height }) => $height }px;
  min-height: ${({ $height }) => $height }px;

  & .recharts-wrapper,
  & .recharts-surface {
    overflow: visible;
  }

  & .recharts-default-tooltip {
    border-radius: ${({ theme }) => theme.border_radius.small }px;
    border-color: ${({ theme }) => theme.glass.border } !important;
    background-color: ${({ theme }) => theme.glass.elevated } !important;
    color: ${({ theme }) => theme.colors.text.default } !important;
  }

  & .recharts-tooltip-label,
  & .recharts-tooltip-item,
  & .recharts-legend-item-text {
    color: ${({ theme }) => theme.colors.text.default } !important;
  }
`;

export const BarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.x_small }px;
`;

export const BarRow = styled.div<{
  $value: number;
  $color?: string;
}>`
  display: grid;
  grid-template-columns: minmax( 140px, 220px ) minmax( 80px, 1fr ) 48px;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.x_small }px;
  font-size: ${({ theme }) => theme.font_size.xx_small }px;

  & > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & div.bar-track {
    height: ${({ theme }) => theme.spacing.x_small }px;
    border-radius: ${({ theme }) => theme.border_radius.large }px;
    overflow: hidden;
    background-color: ${({ theme }) => theme.colors.text.default }18;
  }

  & div.bar-fill {
    width: ${({ $value }) => Math.max( 2, Math.min( 100, $value )) }%;
    height: 100%;
    border-radius: inherit;
    background-color: ${({ theme, $color }) => $color ?? theme.colors.info.default };
  }

  & strong {
    text-align: right;
  }

  @media ( max-width: 640px ) {
    grid-template-columns: minmax( 96px, 1fr ) minmax( 72px, 2fr ) 40px;
  }
`;

export const DailyChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.xx_small }px;
  min-height: 180px;
  padding-top: ${({ theme }) => theme.spacing.x_medium }px;
`;

export const DailyColumn = styled.div<{
  $height: number;
  $showLabel?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  min-width: 10px;
  height: 180px;
  gap: ${({ theme }) => theme.spacing.xx_small }px;

  & div {
    width: 100%;
    min-height: 4px;
    height: ${({ $height }) => Math.max( 4, Math.min( 100, $height )) }%;
    border-radius: ${({ theme }) => theme.border_radius.small }px ${({ theme }) => theme.border_radius.small }px 0 0;
    background: linear-gradient( 180deg, ${({ theme }) => theme.colors.info.default }, ${({ theme }) => theme.colors.info.hover } );
  }

  & span {
    writing-mode: vertical-rl;
    transform: rotate( 180deg );
    min-height: ${({ theme }) => theme.font_size.xxx_large }px;
    color: ${({ theme }) => theme.colors.text.default }99;
    font-size: ${({ theme }) => theme.font_size.x_small }px;
    opacity: ${({ $showLabel }) => $showLabel ? 1 : 0 };
  }
`;

export const RankingTable = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.border_radius.small }px;
  border: 1px solid ${({ theme }) => theme.glass.border };

  & div.row {
    display: grid;
    grid-template-columns: minmax( 140px, 1fr ) 86px 86px 120px;
    gap: ${({ theme }) => theme.spacing.x_small }px;
    padding: ${({ theme }) => theme.spacing.x_small }px;
    border-bottom: 1px solid ${({ theme }) => theme.glass.border };
    font-size: ${({ theme }) => theme.font_size.xx_small }px;
    min-width: 540px;
  }

  & div.row:last-child {
    border-bottom: none;
  }

  & div.header {
    font-weight: 700;
    background-color: ${({ theme }) => theme.colors.text.default }12;
  }
`;

export const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.x_medium }px;
  border-radius: ${({ theme }) => theme.border_radius.small }px;
  border: 1px dashed ${({ theme }) => theme.glass.border };
  background-color: ${({ theme }) => theme.colors.text.default }08;
  color: ${({ theme }) => theme.colors.text.default }B3;
  text-align: center;
`;
